import {
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";

import { ApexOptions, NgApexchartsModule, XAxisAnnotations } from "ng-apexcharts";
import * as ApexCharts from "apexcharts";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { LorenLineChartData } from "../../interfaces/loren-line-chart-data.interface";
import { LorenDatePipe } from "../../../../pipes/loren-date/pipes/loren-date.pipe";
import { LorenMeteringType } from "../../../../../features/loren-assets/interfaces/loren-metering-type";
import {
  LorenDownloadPdfService
} from "../../../../../features/loren-assets-reports/services/loren-download-pdf-service/loren-download-pdf.service";
import { LorenAssetService } from "../../../../../features/loren-assets/services/loren-asset-service/loren-asset.service";
import {
  LorenAuthenticationService
} from "../../../../services/loren-authentication-service/loren-authentication.service";
import { LorenChartNoteDialog } from "../loren-chart-note-dialog/loren-chart-note-dialog.component";
import {
  LorenColor
} from "../../../loren-entity-form/components/loren-entity-form-field/constants/loren-color.constants";
import {
  LorenDoorWindowStateConstants,
  LorenIgnitionStateConstants,
  LorenLightStateConstants,
  LorenPIRStateConstants,
  LorenSmokeStateConstants,
  LorenStateConstants,
  LorenTiltStateConstants,
  LorenWaterLeakStateConstants,
} from "../../../../../features/loren-automation-plans/constants/loren-binary-state.constants";
import { LorenLineChartSeriesItem } from "../../interfaces/loren-line-chart-series-item.interface";
import { LorenLineChartNote } from "../../interfaces/loren-line-chart-note.interface";
import {
  LorenLineChartUtilsService
} from "../../services/loren-line-chart-utils-service/loren-line-chart-utils.service";
import { LorenDateModule } from "../../../../pipes/loren-date/loren-date.module";
import { LorenMetricUnits } from "../../../loren-asset-card/constants/loren-metric-units.constants";
import { LorenMetricBinaryValues } from "../../../loren-asset-card/constants/loren-metric-icons";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatButton } from "@angular/material/button";
import { Duration, Instant } from "@js-joda/core";
import { formatDuration, getLocale } from "../../../../../common/lang";
import {
  getFillLevelTranslationKey,
  isFillLevelSensor,
} from "../../../../../features/asset/fill-level.utils";
import { LorenMeteringContext } from "../../../../../features/loren-assets/enums/loren-metering-context.enum";

export interface LorenLineChartNoteAnnotation {
  x?: number;
  y?: number;
  objectName?: string;
  notes?: LorenLineChartNote[];
  label?: { borderColor: string; text: string };
  marker?: { size: number; fill: string };
}

export interface LorenLineChartAnomalyPeriod {
  start?: number;
  end?: number | null;
  final?: number;
  name?: string;
}

@Component({
  imports: [
    NgApexchartsModule,
    TranslocoPipe,
    LorenDateModule,
    MatButton,
  ],
  selector: "loren-line-chart",
  templateUrl: "./loren-line-chart.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./loren-line-chart.component.scss"],
})
export class LorenLineChartComponent implements OnInit, OnChanges {
  private translateService = inject(TranslocoService);
  private downloadPdfService = inject(LorenDownloadPdfService);
  private assetService = inject(LorenAssetService);
  private authenticationService = inject(LorenAuthenticationService);
  private matDialog = inject(MatDialog);

  private readonly destroyRef = inject(DestroyRef);

  @Input() chartData: LorenLineChartData[];
  @Input() metric: LorenMeteringType;
  @Input() showNotes: boolean;
  @Input() showMinMax: boolean;
  @Input() yMax: number;
  @Input() yMin: number;
  @Input() binary: boolean;
  @Input() binaryContext: LorenMeteringType | LorenMeteringContext;
  @Input() isHandset: boolean;

  @Output() shouldRefresh = new EventEmitter<void>();

  @ViewChild("chart") chart: ApexCharts;

  public chartOptions: ApexOptions;
  private username: string;
  private anomalyPeriods: LorenLineChartAnomalyPeriod[] = [];
  private xAxisAnnotations: XAxisAnnotations[] = [];

  notes: LorenLineChartNoteAnnotation[] = [];
  colors = LorenColor;
  private didZoom = false;

  unit = computed(() => LorenMetricUnits.get(this.metric));

  ngOnInit() {
    this.notes = this.getPointAnnotations();
    if (this.binary && this.binaryContext === LorenMeteringType.DOOR_WINDOW_BINARY_SENSOR) {
      this.chartData.forEach((c) => this.addDurationToBinaryChart(c.series));
    }
    this.setUpChart();
    this.setUpPDFSubscription();
    const { firstName, lastName } = this.authenticationService.getUserInfo();
    this.username = `${firstName} ${lastName}`;
  }

  ngOnChanges() {
    if (this.showNotes === undefined) this.shouldRefresh.emit();
    this.setUpChart();
    this.getNullDurations();
  }

  clickNote(note: LorenLineChartNoteAnnotation) {
    const series = note?.objectName && this.getFullSeries(note?.objectName);
    const item = series?.series?.find((x) => x.x === note.x && x.y === note.y);
    if (!item) return;
    this.handleNoteClick([{ name: series.name, id: series.id, point: item }]);
  }

  /// CHART SETUP
  private setUpChart() {
    const chartDataLength = this.chartData.length;

    this.chartOptions = {
      annotations: {
        points: this.showNotes ? this.getPointAnnotations() : undefined,
        xaxis: this.xAxisAnnotations,
      },
      chart: {
        animations: { enabled: false },
        events: {
          zoomed: () => {
            this.didZoom = true;
          },
          animationEnd: this.setUpPDFSubscription,
          click: (event, chartContext, config) => {
            if (this.didZoom) {
              this.didZoom = false;
              return;
            }
            if (!this.showNotes) return;
            if (event.detail === 2 || event.type === "mouseup") {
              this.clickPointOnChart(chartContext, config);
            }
          },
        },
        defaultLocale: this.translateService.getActiveLang().toLowerCase(),
        height: 350,
        locales: [
          { name: "en" },
          {
            name: "bg",
            options: LorenLineChartUtilsService.lineChartBGLocaleOptions,
          },
        ],
        type: this.showMinMax ? "rangeArea" : "line",
        zoom: {
          allowMouseWheelZoom: false,
          enabled: true,
        },
      },
      colors: this.getColors(),
      dataLabels: {
        enabled: false,
      },
      fill: {
        opacity: this.getOpacity(chartDataLength),
      },
      grid: {
        padding: { left: 12, right: 0, top: 24, bottom: 0 },
      },
      legend: {
        customLegendItems: this.chartData.map((c) => c.name),
        horizontalAlign: "left",
        onItemClick: {
          toggleDataSeries: true,
        },
        onItemHover: {
          highlightDataSeries: true,
        },
        show: true,
        showForSingleSeries: true,
      },
      markers: {
        showNullDataPoints: true,
        size: 3,
      },
      series: this.getSeries(),
      stroke: {
        curve: "straight",
        lineCap: "round",
        show: true,
        width: this.getWidth(chartDataLength),
      },
      tooltip: {
        enabled: true,
        hideEmptySeries: false,
        custom: ({ seriesIndex, dataPointIndex, w }) => {
          const series = w.config.series[seriesIndex];
          const dataPoint = series.data[dataPointIndex];
          return this.createTooltipContent(dataPoint.x);
        },
      },
      xaxis: { type: "datetime", labels: { datetimeUTC: false } },
      yaxis: {
        min: this.yMin,
        max: this.yMax,
        labels: {
          formatter: (value) => this.getYAxisTicks(value),
        },
      },
    };
  }

  private getPointAnnotations() {
    return this.chartData
      .map((c) => c.series.filter((x) => x.notes.length))
      .flat()
      .map((item, i) => {
        const { x, y, objectName, notes } = item;
        return {
          x,
          y,
          objectName,
          notes,
          label: { borderColor: "#000", text: `${i + 1}` },
          marker: {
            size: 4,
            fill: "#000",
          },
        };
      });
  }

  private getColors() {
    return this.showMinMax
      ? Array(2)
        .fill(this.chartData.map((c, i) => this.colors[i].hexCode))
        .flat()
      : this.chartData.map((c, i) => this.colors[i].hexCode);
  }

  private getOpacity(length: number) {
    return this.showMinMax
      ? [...Array(length).fill(1), ...Array(length).fill(0.25)]
      : 1;
  }

  private getSeries() {
    const lines = this.chartData.map((c) => ({
      name: c.name,
      type: "line",
      data: c.series.map((s) => ({ x: s.x, y: s.y })),
      id: c.id,
    }));

    if (!this.binary && this.showMinMax) {
      const ranges = this.chartData.map((c) => ({
        name: `${c.name}--range`,
        type: "rangeArea",
        data: c.series.map((s) => ({
          x: s.x,
          y: s.yArray,
        })),
        id: `${c.id}--range`,
      }));
      return [...lines, ...ranges];
    }

    return lines;
  }

  private getWidth(length: number) {
    return this.showMinMax
      ? [...Array(length).fill(2), ...Array(length).fill(0)]
      : 2;
  }

  private getYAxisTicks(value: number) {
    if (this.binary) {
      const tick = this.getYAxisTicksConstant().get(value ? 1 : 0);
      return this.translateService.translate(tick);
    }

    if (isFillLevelSensor(this.metric)) {
      const translationKey = getFillLevelTranslationKey(value);
      return translationKey
        ? this.translateService.translate(translationKey)
        : value?.toString() ?? "";
    }

    // Apex-generated ticks carry float noise (e.g. -2.7e-17 where 0 belongs);
    // round it off so near-zero renders as blank instead of scientific notation.
    const rounded = Number(value.toFixed(6));
    return rounded ? rounded.toString() : "";
  }

  /// CHART TOOLTIP
  private createTooltipContent(time) {
    const allSeries = this.getSeries().filter((s) => s.type === "line");
    return `<div style='width: max-content; height: max-content;'>
                <div style="display: flex;
                  justify-content: center;
                  background-color: #F3F3F3;
                  padding: 4px;
                  font-weight: 400;
                  font-size: 14px;
                  text-transform: uppercase"
                >${this.transformDate(time)}</div>

                <div style='display: flex; flex-direction: column; gap: 8px; padding: 16px; font-size: 14px'>
                  ${allSeries
      .map((s, i) =>
        this.getSingleSeriesTooltipContent(
          Number(s.id),
          time,
          this.colors[i]
        )
      )
      .join("")}
                </div>
              </div>`;
  }

  private getSingleSeriesTooltipContent(seriesId: number, time, color) {
    const series = this.getFullSeriesById(seriesId);
    const point = series.series.find((s) => s.x === time);
    if (!point) return;

    return `
      <div style="display: flex; align-items: center; font-size: 12px">
        <div style="display: inline-block; height: 16px; width: 16px; margin-right: 8px; border-radius: 50%; background-color: ${
      color.hexCode
    }">
        </div>
        <span>${series.name}</span>: &nbsp;
        <strong>
          ${[
      this.showMinMax && point.min !== null && point.min !== undefined
        ? `<span class="text-info">${this.getTooltipValue(point.min)}${
          this.unit() || ""
        }</span>`
        : null,
      `<span class="">${
        this.binary
          ? this.translateService.translate(
            LorenMetricBinaryValues.get(this.binaryContext)[
              Boolean(point.y).toString()
              ]
          )
          : this.getTooltipValue(point.y)
      }${this.unit() || ""}</span>`,
      this.showMinMax && point.max !== null && point.max !== undefined
        ? `<span class="text-warning">${this.getTooltipValue(point.max)}${
          this.unit() || ""
        }</span>`
        : null,
    ]
      .filter((i) => i)
      .join(" / ")}
          ${
      this.binary &&
      this.binaryContext === LorenMeteringType.DOOR_WINDOW_BINARY_SENSOR
        ? `<div>${this.getDurationOpenedForTooltip(point)}</div>`
        : ""
    }
        </strong>
        </div>`;
  }

  private getDurationOpenedForTooltip(point: LorenLineChartSeriesItem): string {
    const locale = getLocale(this.translateService.getActiveLang())

    if (!point.duration) {
      return '';
    }

    const formatted = formatDuration(locale, point.duration);

    if (point.y === 1) {
      return this.translateService.translate("chart.closed_for", { duration: formatted });
    } else {
      return this.translateService.translate("chart.opened_for", { duration: formatted });
    }
  }

  private getTooltipValue(value: number | null) {
    if (value === null || value === undefined) {
      return "";
    }

    if (isFillLevelSensor(this.metric)) {
      const translationKey = getFillLevelTranslationKey(value);
      return translationKey
        ? this.translateService.translate(translationKey)
        : value.toFixed(2);
    }

    return value.toFixed(2);
  }

  /// NOTES
  private clickPointOnChart = (chartContext, config) => {
    const clickedSeries = this.findClickedSeries(config);
    const date = chartContext.data.twoDSeriesX[config.dataPointIndex];
    if (!date) return;
    const series = this.mapSeriesForNote(config?.config.series, date);

    this.handleNoteClick(series, clickedSeries);
  };

  private findClickedSeries(config) {
    const { seriesIndex, dataPointIndex } = config;
    if (seriesIndex !== -1 && dataPointIndex !== -1) {
      return this.chartOptions.series![seriesIndex];
    } else return null;
  }

  private handleNoteClick(
    series: {
      name: string;
      id: number;
      point: LorenLineChartSeriesItem;
    }[],
    clickedSeries = null
  ) {
    const dialog = this.getDialog(series, clickedSeries);

    dialog.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((result) => {
      if (!result?.note?.trim()) return;
      const point = series.find((s) => s.id === result.metering).point;

      this.addNote(result.metering, point, result.note).subscribe(() => {
        this.setUpChart();
        this.shouldRefresh.emit();
      });
    });
  }

  private mapSeriesForNote(series, date) {
    return series
      .filter((i) => !i.name.includes("--range"))
      .map((i) => {
        const fullSeries = this.getFullSeries(i.name);
        if (!fullSeries) return;
        return {
          name: i.name,
          id: fullSeries.id,
          point: fullSeries.series.find((s) => s.x === date),
        };
      })
      .filter((s) => s.point);
  }

  private getDialog(series, clickedSeries) {
    const dialogRef = this.matDialog.open(LorenChartNoteDialog, {
      width: this.isHandset ? "80%" : "50%",
      data: {
        notes: series.map((s) => s.point?.notes).filter((n) => n?.length),
        meterings: series.map((s, i) => ({
          name: s.name,
          id: s.id,
          color: this.colors[i].hexCode,
        })),
        clickedSeries,
      },
    });
    return dialogRef.afterClosed();
  }

  private addNote(metering: number, item, note: string) {
    const noteObj = {
      author: this.username,
      note,
      forHour: item.x && new Date(item.x).toISOString(),
    };

    const currentNotePoint = this.notes.find(
      (note) =>
        note.objectName === item.objectName &&
        note.x === item.x &&
        note.y === item.y
    );

    if (currentNotePoint) {
      currentNotePoint.notes.push(noteObj);
    } else {
      const notesFullObj = {
        x: item.x,
        y: item.y,
        objectName: item.objectName,
        notes: [noteObj],
      };
      this.notes.push(notesFullObj);
      this.chart.addPointAnnotation(notesFullObj, true);
    }
    return this.assetService.addNote(metering, noteObj);
  }

  /// MISC
  private getFullSeries(name: string) {
    return this.chartData.find(
      (c) => c.name === (name.includes("--range") ? name.split("--")[0] : name)
    );
  }

  private getFullSeriesById(id: number) {
    return this.chartData.find((c) => c.id === id);
  }

  private setUpPDFSubscription() {
    if (
      this.metric === LorenMeteringType.HUMIDITY_SENSOR ||
      this.metric === LorenMeteringType.TEMPERATURE_SENSOR
    ) {
      this.downloadPdfService.trigger.pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((x) => {
        if (x) {
          this.chart.dataURI().then((svg) => {
            this.downloadPdfService.updatePayload({
              ids: this.chartData.map((data) => data.id),
              svg,
            });
          });
        }
      });
    }
  }

  private transformDate(string: string) {
    return new LorenDatePipe().transform(string);
  }

  /// There is an additional 1 sec before a switch for opening/closing doors.
  /// This adds the duration for which the door was open,
  /// calculating the initial opening and the additional second,
  /// and adding them to both points.
  private addDurationToBinaryChart(series: LorenLineChartSeriesItem[]) {
    series?.forEach((curr, i) => {
      const prev = series[i - 1];

      if (!prev) {
        return null;
      }

      if ([undefined, null].includes(curr.y)) {
        return null;
      }

      if ([undefined, null].includes(prev.y)) {
        return null;
      }

      if (curr.y !== prev.y) {
        // Do not calculate duration for state change entries.
        // Only the last dot of the state should have the duration
        return null;
      }

      const currDate = Instant.ofEpochMilli(curr.x);
      const prevDate = Instant.ofEpochMilli(prev.x);

      curr.duration = Duration.between(prevDate, currDate);
    });
  }

  private getNullDurations() {
    this.chartData.forEach((c) => {
      this.getNullDuration(c.series);
    });

    this.xAxisAnnotations = this.anomalyPeriods.map((a) => ({
      borderColor: "#ff0000",
      fillColor: "#ffb3b3",
      label: {
        borderColor: "#ffb3b3",
        style: {
          color: "#ff0000",
        },
        text: this.translateService.translate("chart.anomaly_detected"),
      },
      x: a.start,
      x2:
        a.start !== a.end && a.start !== a.final ? a.end || a.final : undefined,
    }));
  }

  private getNullDuration(series: LorenLineChartSeriesItem[]) {
    const anomalyStart = series.findIndex((s) => s.anomaly);
    const rest = [...series].slice(anomalyStart, series.length);
    const anomalyEnd = rest.findIndex((s) => !s.anomaly);
    const anomaly = {
      start: series[anomalyStart]?.x,
      end: anomalyEnd !== -1 ? rest[anomalyEnd - 1]?.x : null,
      final: series[series.length - 1]?.x,
      name: series[0].objectName,
    };
    this.anomalyPeriods.push(anomaly);
    if (anomalyEnd !== -1) {
      const remainder = [...rest].slice(anomalyEnd, rest.length);
      if (remainder.some((s) => s.anomaly)) this.getNullDuration(remainder);
    }
  }

  private getYAxisTicksConstant() {
    switch (this.binaryContext) {
      case LorenMeteringType.PIR_BINARY_SENSOR:
        return LorenPIRStateConstants;
      case LorenMeteringType.LIGHT_BINARY_SENSOR:
        return LorenLightStateConstants;
      case LorenMeteringType.SMOKE_BINARY_SENSOR:
        return LorenSmokeStateConstants;
      case LorenMeteringType.IGNITION_BINARY_SENSOR:
        return LorenIgnitionStateConstants;
      case LorenMeteringType.TILT_BINARY_SENSOR:
        return LorenTiltStateConstants;
      case LorenMeteringType.WATER_LEAK_BINARY_SENSOR:
        return LorenWaterLeakStateConstants;
      case LorenMeteringContext.STATE:
        return LorenStateConstants;
      default:
        return LorenDoorWindowStateConstants;
    }
  }
}
